import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { type AvailableUnit } from '@/core/api/models/properties';
import {
  useAppStore,
  type PendingUnitReservation,
} from '@/core/stores/appStore';
import { useSyncProfileDetailsToStore } from '@/modules/auth/hooks/useSyncProfileDetailsToStore';
import { profileService } from '@/modules/auth/services/profileService';
import { bookingService } from '../services/bookingService';
import {
  applyAdhaBookingApiResult,
  formatBookingRequestedOn,
} from '../utils/syncAdhaBookingFromApi';

export type ListingUnit = {
  unit_id: string;
  unit_number: string;
  project_name?: string;
  project_id?: string;
  unit_type?: string;
  /** Matches a Contentful `unitModel` key. Absent until the listing API sends it. */
  unit_model?: string;
  number_of_bedrooms: string;
  number_of_bathrooms: number;
  total_area: number;
  total_price: number;
  handover_date?: string;
};

export function listingUnitTitle(
  listing?: ListingUnit,
  fallbackProjectName?: string,
  fallbackProjectLocation?: string,
  contentfulProjectName?: string,
): string {
  if (!listing) {
    return '';
  }
  // Contentful name → unit API project_name → profile/loan fallback
  const projectName =
    contentfulProjectName?.trim() ||
    listing.project_name?.trim() ||
    fallbackProjectName?.trim() ||
    '';

  return [listing.unit_number, projectName, fallbackProjectLocation]
    .filter(Boolean)
    .join(', ');
}

/**
 * Listing: sync reservation from GET /v1/bookings, enriched with profile-details
 * (`booking_date`, `current_status`) when the booking unit matches.
 */
export function useAdhaReservationStatus(
  customerId: string | null | undefined,
  pendingUnitReservation: PendingUnitReservation | null,
  projectNameFromProfile: string,
  projectLocationFromProfile: string,
) {
  const setPendingUnitReservation = useAppStore(
    (s) => s.setPendingUnitReservation,
  );
  const clearAdhaWebBookingState = useAppStore(
    (s) => s.clearAdhaWebBookingState,
  );
  const upsertAvailableUnitFirst = useAppStore(
    (s) => s.upsertAvailableUnitFirst,
  );

  const bookingQuery = useQuery({
    queryKey: ['adhaBooking', customerId],
    queryFn: () => bookingService.getBookingDetails(customerId!),
    enabled: !!customerId,
    retry: false,
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const profileQuery = useQuery({
    queryKey: ['adhaProfileDetails', customerId],
    queryFn: () => profileService.getProfileDetails(),
    enabled: !!customerId,
    retry: false,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  useSyncProfileDetailsToStore(
    profileQuery.data,
    profileQuery.isSuccess,
  );

  useEffect(() => {
    if (!customerId || !bookingQuery.isFetched) {
      return;
    }

    const availableUnits = useAppStore.getState().availableUnits;
    const resolveUnitTitle = (unit: AvailableUnit) =>
      listingUnitTitle(
        unit as ListingUnit,
        projectNameFromProfile,
        projectLocationFromProfile,
      );

    if (bookingQuery.isError) {
      clearAdhaWebBookingState();
      return;
    }

    const profileData = profileQuery.isSuccess ? profileQuery.data : null;

    const hasActiveBooking = applyAdhaBookingApiResult({
      response: bookingQuery.data,
      availableUnits,
      resolveUnitTitle,
      formatRequestedOn: formatBookingRequestedOn,
      profileDetails: profileData,
      setPendingUnitReservation,
      clearAdhaWebBookingState,
      getPreviousPending: () => useAppStore.getState().pendingUnitReservation,
      upsertAvailableUnitFirst,
    });

    if (!hasActiveBooking) {
      return;
    }
  }, [
    profileQuery.data,
    profileQuery.isSuccess,
    bookingQuery.data,
    bookingQuery.isError,
    bookingQuery.isFetched,
    clearAdhaWebBookingState,
    customerId,
    projectLocationFromProfile,
    projectNameFromProfile,
    setPendingUnitReservation,
    upsertAvailableUnitFirst,
  ]);

  return { bookingQuery, profileQuery };
}
